// workoutLog.js — ONE workout log (2026-10-22). The Gym habit's tick sheet and
// Health's "+ Log workout" both build rows here and write the same workouts
// table; week counts (src/lib/workoutSessions.js) count a tick on a day with a
// saved workout as that same session, so a tick plus a saved workout on one
// day is one workout everywhere. Pure.
import { fmtDueDay } from './commitmentDisplay.js';

// The shared type list, both entry points (Custom is gone: Other + a note).
export const WORKOUT_TYPES = ['Lift', 'Run', 'Walk', 'Cycle', 'Swim', 'Yoga', 'Sport / Martial arts', 'Other'];

// Workout-type habits: Gym gets the details sheet; Stretch gets the plain note.
export const WORKOUT_SHEET_HABITS = new Set(['gym']);

/**
 * Ask for details after this habit tick? Only the Gym habit, only when no
 * workout row (any source: a watch sync counts) exists for that local date.
 */
export function shouldAskWorkoutDetails({ habitId, workouts = [], date } = {}) {
  if (!WORKOUT_SHEET_HABITS.has(habitId) || !date) return false;
  return !(workouts || []).some(w => w && String(w.date || '').slice(0, 10) === date);
}

/** The workouts row for an insert. Blank optional fields are null, never "". */
export function buildWorkoutRow({ userId, type, durationMin, note, date, name, distanceMi } = {}) {
  const t = String(type || '').trim();
  const d = parseInt(durationMin, 10);
  const mi = distanceMi !== undefined && distanceMi !== null && String(distanceMi).trim() !== '' ? parseFloat(distanceMi) : null;
  return {
    user_id: userId,
    type: t || 'Other',
    name: String(name || '').trim() || null,
    duration_min: Number.isFinite(d) && d > 0 ? d : null,
    distance_m: Number.isFinite(mi) && mi >= 0 ? Math.round(mi * 1609.34) : null,
    notes: String(note || '').trim() || null,
    date,
  };
}

// Days back (0 = today) a workout may still tick the Gym habit for its date;
// older backdated workouts count toward the goal and the Health score through
// the day-dedupe, but never touch the habit or its streak.
export const HABIT_TICK_MAX_DAYS_BACK = 2;
const dayMs = (d) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || '')); return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : null; };
/** The date the Gym habit should be ticked for this workout, or null. */
export function habitTickDateForWorkout({ date, today } = {}) {
  const a = dayMs(date), b = dayMs(today);
  if (a == null || b == null) return null;
  const back = Math.round((b - a) / 86400000);
  return back >= 0 && back <= HABIT_TICK_MAX_DAYS_BACK ? String(date).slice(0, 10) : null;
}

/** "Today", "Yesterday", a weekday within the week, else "Thu, Oct 22" — never ISO. */
export const fmtWorkoutDate = (date, today) => fmtDueDay(date, today) || '';

/** The Workouts card line under the type: duration · note-free; newest first is the caller's sort. */
export function workoutMeta(w, today) {
  const parts = [];
  if (w?.duration_min) parts.push(`${w.duration_min} min`);
  if (w?.distance_m) parts.push(`${(Number(w.distance_m) / 1609.34).toFixed(1)} mi`);
  const d = fmtWorkoutDate(w?.date, today);
  if (d) parts.push(d);
  return parts.join(' · ');
}

// ── AFTER-THE-FACT DETAILS (2026-10-22) ──────────────────────────────────────
/** A workout-type habit: "+ note" / long-press open the workout sheet, not the plain note. */
export const isWorkoutHabit = (habitId) => WORKOUT_SHEET_HABITS.has(habitId);

/**
 * The workout type a free-text note implies. MATCHED PAIR with the CASE in
 * supabase/migrations/20261022_habit_notes_to_workouts.sql (the one-time
 * migration of existing Gym notes) — change both or neither.
 */
export function workoutTypeFromNote(note) {
  const n = String(note || '').toLowerCase();
  if (/muay\s*thai|box(?:ing)?\b|\bbjj\b|jiu[\s-]*jitsu|\bmma\b|kick\s*box|martial|karate|judo|wrestl|sparr|taekwondo|\bsport\b|basketball|soccer|tennis|golf|pickleball|climb/.test(n)) return 'Sport / Martial arts';
  if (/\blift|weights?\b|\bgym\b|squat|bench|deadlift|strength|push day|pull day|leg day|upper|lower body/.test(n)) return 'Lift';
  if (/\brun|jog|sprint|treadmill|\b5k\b|\b10k\b/.test(n)) return 'Run';
  if (/\bwalk|hike|hiking|steps\b/.test(n)) return 'Walk';
  if (/\bbike|cycl|spin\b|peloton|ride\b/.test(n)) return 'Cycle';
  if (/\bswim|\blaps\b|pool\b/.test(n)) return 'Swim';
  if (/\byoga|pilates|stretch|mobility/.test(n)) return 'Yoga';
  return 'Other';
}

// ── CONFIRMED WORKOUT COMMITMENTS (2026-10-24) ───────────────────────────────
// "Did Muay Thai happen?" → Yes (and "Mark done", same path) used to resolve only the coach_commitments row,
// so the day showed no workout and no Gym tick (rock, Oct 23). A confirmed
// workout-type commitment now lands the same two things Health's "+ Log
// workout" does, for the commitment's local date: the day's workouts row (if
// none exists that day, any source) and the Gym habit tick (today or up to 2
// days back). "No, it didn't" never reaches here. Planner commitments
// (source 'plan', metric_type 'workout') take the same path.
// MATCHED PAIR with supabase/migrations/20261024_confirmed_workout_commitments.sql
// (the one-time backfill of already-confirmed rows) — change both or neither.
export const isWorkoutCommitment = (c) => !!c && c.metric_type === 'workout';

/**
 * What confirming a workout commitment should write.
 * @param {object} p
 * @param {object} p.commitment  coach_commitments row (text, due_date, metric_type, metric_ref)
 * @param {Array}  p.workouts    the user's loaded workouts rows (any source)
 * @param {string} p.today       local YYYY-MM-DD
 * @returns {null | { date: string, row: null | { type: string, note: string, date: string }, tickDate: string | null }}
 *   null = not a workout commitment (nothing to write). `row` is null when the
 *   day already has a workout (a watch sync, a Health log): no duplicate.
 */
export function workoutWritesForConfirmed({ commitment, workouts = [], today } = {}) {
  if (!isWorkoutCommitment(commitment)) return null;
  // The commitment's local date; a plan marked done BEFORE its day (Mark done
  // on tomorrow's workout) happened today, never in the future.
  const due = String(commitment.due_date || '').slice(0, 10);
  const now = String(today || '').slice(0, 10);
  const date = due && now && due > now ? now : (due || now);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const title = String(commitment.text || '').trim();
  // Type from the title; a generic title ("Workout") falls back to the specific
  // activity the commitment was recorded against (metric_ref 'muay thai').
  let type = workoutTypeFromNote(title);
  if (type === 'Other' && commitment.metric_ref) type = workoutTypeFromNote(commitment.metric_ref);
  const exists = (workouts || []).some(w => w && String(w.date || '').slice(0, 10) === date);
  return {
    date,
    row: exists ? null : { type, note: title, date },
    tickDate: habitTickDateForWorkout({ date, today }),
  };
}

/** The sheet's starting fields for a habit day: edit the existing row, or create one. */
export function workoutSheetFor({ habitId, date, workouts = [], habitNote = null } = {}) {
  const row = (workouts || []).find(w => w && String(w.date || '').slice(0, 10) === date);
  if (row) return { habitId, date, editId: row.id, type: row.type || 'Other', duration: row.duration_min ? String(row.duration_min) : '', note: row.notes || '' };
  return { habitId, date, editId: null, type: habitNote ? workoutTypeFromNote(habitNote) : 'Lift', duration: '', note: habitNote || '' };
}
